// SectionHeader — the standardized "page philosophy" header every page should begin with.
// Title, short explanation, optional insight chips, optional primary action. THEN the table.

import { ElementType } from 'react';
import { Box, Button, Chip, Stack, Typography } from '@mui/material';
import { UI } from '../../brand/ui';

export function SectionHeader({
  title,
  description,
  insights,
  actionLabel,
  onAction,
  actionIcon: ActionIcon
}: {
  title: string;
  description?: string;
  insights?: Array<{ label: string; tone?: 'default' | 'success' | 'warning' | 'error' }>;
  actionLabel?: string;
  onAction?: () => void;
  actionIcon?: ElementType<{ size?: number }>;
}) {
  return (
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      justifyContent="space-between"
      alignItems={{ xs: 'stretch', sm: 'flex-start' }}
      spacing={2}
      sx={{
        mb: 2,
        p: 2.25,
        borderRadius: '12px',
        border: `1px solid ${UI.border}`,
        bgcolor: UI.white,
        boxShadow: UI.shadowSm
      }}
    >
      <Box>
        <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1.2, color: UI.ink }}>
          {title}
        </Typography>
        {description ? (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, maxWidth: 600 }}>
            {description}
          </Typography>
        ) : null}
        {insights && insights.length > 0 ? (
          <Stack direction="row" spacing={1} sx={{ mt: 1.5, flexWrap: 'wrap', gap: 1 }}>
            {insights.map((chip, i) => (
              <Chip
                key={i}
                size="small"
                label={chip.label}
                color={chip.tone === 'success' ? 'success' : chip.tone === 'warning' ? 'warning' : chip.tone === 'error' ? 'error' : 'default'}
                variant={chip.tone && chip.tone !== 'default' ? 'filled' : 'outlined'}
                sx={{ fontWeight: 600 }}
              />
            ))}
          </Stack>
        ) : null}
      </Box>
      {actionLabel && onAction ? (
        <Button variant="contained" startIcon={ActionIcon ? <ActionIcon size={18} /> : undefined} onClick={onAction} sx={{ flexShrink: 0, alignSelf: { xs: 'flex-start', sm: 'center' } }}>
          {actionLabel}
        </Button>
      ) : null}
    </Stack>
  );
}
